import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { canAccessPath, canViewProductLinks, COUNTER_HOME_PATH } from './config/permissions';

// Pages
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import EmployeePerformancePage from './pages/EmployeePerformancePage';
import ProductsPage from './pages/ProductsPage';
import SettingsPage from './pages/SettingsPage';
import ShopeeCallbackPage from './pages/auth/ShopeeCallbackPage';
import ShopifyCallbackPage from './pages/auth/ShopifyCallbackPage';
import NotFoundPage from './pages/NotFoundPage';
import ShopifyInvoicePage from './pages/ShopifyInvoicePage'

// Components
import Layout from './components/Layout';
import { AuthScreen } from './components/AuthScreen';
import AddOutletPage from './pages/AddOutletPage';
import OutletManager from './pages/OutletManager';
import OutletStorePage from './pages/OutletStorePage';
import ShippingOrderPage from './pages/ShippingOrderPage';
import ProductTagManager from './components/ProductTagManager';
import TaskTrackingPage from './pages/TaskTrackingPage';
import OwnerBoardPage from './pages/OwnerBoardPage';
import AgentThreadPage from './pages/AgentThreadPage';
import HitRadarPage from './pages/HitRadarPage';
import RadarToolPage from './pages/RadarToolPage';
import ScoutFeedPage from './pages/ScoutFeedPage';
import MarkdownPatrolPage from './pages/MarkdownPatrolPage';
import FukubukuroAdminPage from './pages/FukubukuroAdminPage';
import StorefrontOrdersPage from './pages/StorefrontOrdersPage';
import ManualStorefrontOrderPage from './pages/ManualStorefrontOrderPage';
import ExchangeReturnManagemetPage from './pages/ExchangeReturnManagementPage'
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import CustomerRetentionPage from './pages/CustomerRetentionPage';
import ReceivingQueuePage from './pages/ReceivingQueuePage';
import StoresPage from './pages/StoresPage';
import LooksAdminPage from './pages/LooksAdminPage';
import ReviewsAdminPage from './pages/ReviewsAdminPage';
import CreditsAdminPage from './pages/CreditsAdminPage';
import GiftPromosPage from './pages/GiftPromosPage';
import InventoryHealthPage from './pages/InventoryHealthPage';
import DaigouOrdersPage from './pages/DaigouOrdersPage';
import MisShipmentPage from './pages/MisShipmentPage';
import NokeCounterPage from './pages/NokeCounterPage';
import NokePosPage from './pages/NokePosPage';
import NokeCampaignsPage from './pages/NokeCampaignsPage';
import NokeTimeClockPage from './pages/NokeTimeClockPage';
import ThreadsPatrolPage from './pages/ThreadsPatrolPage';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4c5e8a',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>; // You might want to create a proper loading component
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

// Role gate for limited accounts. A `counter` user (NOKE 門市人員) may open only
// the two NOKE screens; any other path — typed, bookmarked, or reached by a
// stale redirect — lands back on the counter page. The backend's counterAuth is
// the real gate; this just keeps the UI from showing shells it cannot fill.
const RoleGate = ({ children }: { children: React.ReactNode }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!canAccessPath(currentUser?.role, location.pathname)) {
    return <Navigate to={COUNTER_HOME_PATH} replace />;
  }

  return <>{children}</>;
};

// Owner-only Route Component. The sidebar already hides these links for staff
// (OWNER_ONLY_PATHS in Layout.tsx), but the URL stays typeable — this sends
// anyone else back to the dashboard. The backend's ownerAuth is the real gate.
const OWNER_USER_ID = 1;

const OwnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (currentUser?.id !== OWNER_USER_ID) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Sourcing pages (新款雷達 全部款): the product-link allowlist (Magnus + Adam). The backend's
// productLinksAuth is the real gate.
const SourcingRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!canViewProductLinks(currentUser?.id)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Public Route Component (redirects to home if already authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>; // You might want to create a proper loading component
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

// Separate component for routes to use the useAuth hook
function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public authentication routes */}
        <Route path="/auth" element={
          <PublicRoute>
            <AuthScreen />
          </PublicRoute>
        } />
        
        {/* External authentication callback routes - these don't need authentication */}
        <Route path="/auth/shopee/callback" element={<ShopeeCallbackPage />} />
        <Route path="/auth/shopify/callback" element={<ShopifyCallbackPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <RoleGate>
              <Layout />
            </RoleGate>
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="shipping-orders" element={<ShippingOrderPage />} />
          <Route path="performance" element={
            <OwnerRoute>
              <EmployeePerformancePage />
            </OwnerRoute>
          } />
          <Route path="products" element={<ProductsPage />} />
          <Route path="tag-management" element={<ProductTagManager />} />
          <Route path="outlet-add" element={<AddOutletPage />} />
          <Route path="/outlet" element={<OutletStorePage />} />
          <Route path="admin-outlet" element={<OutletManager />} />
          <Route path="tasks" element={<TaskTrackingPage />} />
          <Route path="scout" element={
            <SourcingRoute>
              <ScoutFeedPage />
            </SourcingRoute>
          } />
          <Route path="owner-board" element={
            <OwnerRoute>
              <OwnerBoardPage />
            </OwnerRoute>
          } />
          <Route path="owner-board/radar" element={
            <OwnerRoute>
              <HitRadarPage />
            </OwnerRoute>
          } />
          <Route path="owner-board/tools/:toolKey" element={
            <OwnerRoute>
              <RadarToolPage />
            </OwnerRoute>
          } />
          <Route path="owner-board/t/:threadId" element={
            <OwnerRoute>
              <AgentThreadPage />
            </OwnerRoute>
          } />
          {/* Claude 工作台 was merged into 每日看板 */}
          <Route path="claude-workbench" element={<Navigate to="/owner-board" replace />} />
          <Route path="markdown-patrol" element={
            <OwnerRoute>
              <MarkdownPatrolPage />
            </OwnerRoute>
          } />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="/fukubukuro" element={<FukubukuroAdminPage />} />
          <Route path="/return-management" element={<ExchangeReturnManagemetPage />} />
          <Route path="/shopify-invoice" element={<ShopifyInvoicePage />} />
          <Route path="/storefront-orders" element={<StorefrontOrdersPage />} />
          <Route path="/manual-order" element={<ManualStorefrontOrderPage />} />
          <Route path="receiving" element={<ReceivingQueuePage />} />
          <Route path="stores" element={<StoresPage />} />
          <Route path="analytics" element={<AnalyticsDashboardPage />} />
          <Route path="retention" element={<CustomerRetentionPage />} />
          <Route path="looks" element={<LooksAdminPage />} />
          <Route path="admin/reviews" element={<ReviewsAdminPage />} />
          <Route path="admin/credits" element={<CreditsAdminPage />} />
          <Route path="admin/gift-promos" element={<GiftPromosPage />} />
          <Route path="inventory-health" element={<InventoryHealthPage />} />
          <Route path="daigou-orders" element={<DaigouOrdersPage />} />
          <Route path="mis-shipments" element={<MisShipmentPage />} />
          <Route path="noke" element={<NokeCounterPage />} />
          <Route path="noke/pos" element={<NokePosPage />} />
          <Route path="noke/clock" element={<NokeTimeClockPage />} />
          {/* 活動折扣 is deliberately NOT in COUNTER_ALLOWED_PATHS — RoleGate
              bounces 門市人員 off it, and every write is admin-gated server-side. */}
          <Route path="noke/campaigns" element={<NokeCampaignsPage />} />
          {/* Threads 海巡 — outside /noke on purpose: the work isn't counter
              work, the counter is just where the dead time is. */}
          <Route path="threads-patrol" element={<ThreadsPatrolPage />} />
        </Route>
        
        {/* Redirect /login to /auth for backward compatibility */}
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        
        {/* 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;